import { useMemo, useRef } from 'react';
import { TABLE_TYPE_SITNGO } from 'constants/gameConstants';
import { useFeatureFlag } from 'featureFlags';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';

import {
  deriveSitGoWaiting,
  HeldLayoutSeat,
  HeldWaitingSeats,
  holdLayoutSeat,
  holdWaitingSeats,
  isSitGoWaitingCandidate,
  readDrawnLayoutSeat,
  readWaitingSeatsThroughStart,
  SitGoWaitingState,
  WaitingRegistrant
} from './sitGoWaitingState';

// The table's own viewer identity (the one its seatId is derived from),
// falling back to the session's while the slice has none.
const useViewerGuid = (): string | undefined => {
  const tableGuid = useTableSelector((table) => table.currentPlayer?.playerGUID);
  const userGuid = useAppSelector((state) => (state as any).user?.playerGUID);
  return tableGuid || userGuid;
};

/**
 * The waiting state, subscribed narrowly: the status feed is pushed to every
 * open table about once a second, so a table that cannot be a filling Sit & Go
 * (or a caller that is switched off) selects nothing from it and does not
 * re-render on it. The lobby list is read only by the one caller that shows a
 * buy-in.
 */
const useWaitingState = (active: boolean, withLobbyBuyIn: boolean): SitGoWaitingState | null => {
  const game = useTableSelector((table) => table.game);
  const candidate = active && isSitGoWaitingCandidate(game);
  const players = useTableSelector((table) => (candidate ? table.players : undefined));
  const tournamentStatus = useTableSelector((table) =>
    candidate ? table.tournamentStatus : undefined
  );
  const guid = useViewerGuid();
  const sitAndGoList = useAppSelector((state) =>
    candidate && withLobbyBuyIn ? (state as any).lobby?.tournament?.sitAndGoList : undefined
  );
  return useMemo(
    () =>
      candidate
        ? deriveSitGoWaiting({ game, players, tournamentStatus, guid, sitAndGoList })
        : null,
    [candidate, game, players, tournamentStatus, guid, sitAndGoList]
  );
};

/**
 * This table's Sit & Go waiting state whatever the flag says, with its buy-in
 * — for the waiting panel, whose flag exposure records the off path at the
 * same surface as the on path. Feature code reads useSitGoWaiting instead.
 */
export const useSitGoPreStart = (): SitGoWaitingState | null => useWaitingState(true, true);

/**
 * EN-11257: the table's Sit & Go waiting state with
 * release.sit-go-seated-waiting on; always null with it off, which leaves the
 * Open seats, the layout and the pre-start modal exactly as they were. Carries
 * no lobby buy-in fallback (see useSitGoPreStart).
 */
export const useSitGoWaiting = (): SitGoWaitingState | null =>
  useWaitingState(useFeatureFlag('sitGoSeatedWaiting'), false);

/**
 * EN-11257: the Sit & Go registrants Seats draws in empty seats, by seat, held
 * through the start until the real seats arrive (see holdWaitingSeats);
 * undefined with release.sit-go-seated-waiting off. The status feed is
 * selected only while the table can still need it: once real seats arrive,
 * or the table ends, this stops re-rendering Seats on every status push.
 */
export const useWaitingSeatOccupants = (): ReadonlyMap<number, WaitingRegistrant> | undefined => {
  const enabled = useFeatureFlag('sitGoSeatedWaiting');
  const game = useTableSelector((table) => table.game);
  const hasRealSeats = useTableSelector(
    (table) => Array.isArray(table?.players) && table.players.slice(1).some((seat: any) => seat?.ID)
  );
  const active =
    enabled &&
    game?.tabletype === TABLE_TYPE_SITNGO &&
    game?.status !== 'F' &&
    game?.status !== 'C' &&
    !hasRealSeats;
  const tournamentStatus = useTableSelector((table) =>
    active ? table.tournamentStatus : undefined
  );
  const guid = useViewerGuid();
  const read = useMemo(
    () => (active ? readWaitingSeatsThroughStart({ game, tournamentStatus }, guid) : null),
    [active, game, tournamentStatus, guid]
  );
  const heldRef = useRef<HeldWaitingSeats | null>(null);
  const { seats, held } = holdWaitingSeats(Number(game?.tournamentId) || 0, read, heldRef.current);
  // Written during render, like useLayoutSeatId's hold: a pure function of the
  // inputs and the previous hold.
  heldRef.current = held;
  return seats;
};

/**
 * The seat the felt rotates around (see holdLayoutSeat). Every input is
 * selected as a primitive, so the seat-rotation provider re-renders only when
 * one changes, not on every table broadcast. Read once, by
 * SeatRotationProvider, which hands it to Seats through the rotation context:
 * the held seat is memory, and two holders could disagree.
 */
export const useLayoutSeatId = (): number => {
  const enabled = useFeatureFlag('sitGoSeatedWaiting');
  const realSeatId: number = useTableSelector((table) => table?.currentPlayer?.seatId);
  const tournamentId = useTableSelector((table) => Number(table?.game?.tournamentId) || 0);
  const drawnSeat = useTableSelector((table) =>
    enabled && !(table?.currentPlayer?.seatId >= 1)
      ? readDrawnLayoutSeat(table, table?.currentPlayer?.playerGUID)
      : null
  );
  const heldRef = useRef<HeldLayoutSeat | null>(null);
  const { seatId, held } = holdLayoutSeat(realSeatId, tournamentId, drawnSeat, heldRef.current);
  // Written during render, like SeatRotationProvider's lastSeenPovRef: the
  // result is a pure function of the inputs and the previous hold, so a
  // repeated render with the same inputs writes the same value.
  heldRef.current = held;
  return seatId;
};
